import { Clock, Wrench, Layers, Activity, ShieldAlert, Drill, Box, Ruler, Hash, Zap, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const LEVEL = {
  low:       { key: "industrialMetrics.lvlLow",       cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  medium:    { key: "industrialMetrics.lvlMedium",    cls: "bg-amber-500/10 text-amber-300 border-amber-500/40" },
  high:      { key: "industrialMetrics.lvlHigh",      cls: "bg-red-500/10 text-red-300 border-red-500/40" },
  easy:      { key: "industrialMetrics.lvlEasy",      cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  difficult: { key: "industrialMetrics.lvlDifficult", cls: "bg-red-500/10 text-red-300 border-red-500/40" },
};

function LevelPill({ value }) {
  const { t } = useTranslation();
  const d = LEVEL[value];
  if (!d) return <span className="text-xs text-slate-500 font-mono-data">{value || "—"}</span>;
  return <span className={`text-[10px] label-tech px-2 py-0.5 rounded border ${d.cls}`}>{t(d.key)}</span>;
}

export default function IndustrialMetrics({ metrics, productionRisks }) {
  const { t } = useTranslation();
  if (!metrics) return null;

  const fields = [
    { k: "setup_minutes",        label: t("industrialMetrics.fSetup"),       icon: <Clock size={12}/>,   suffix: " min", isNumber: true },
    { k: "fixturing_minutes",    label: t("industrialMetrics.fFixturing"),   icon: <Wrench size={12}/>,  suffix: " min", isNumber: true },
    { k: "tools_count",          label: t("industrialMetrics.fTools"),       icon: <Drill size={12}/>,   isNumber: true },
    { k: "setups_count",         label: t("industrialMetrics.fSetups"),      icon: <Layers size={12}/>,  isNumber: true },
    { k: "feature_count",        label: t("industrialMetrics.fFeatures"),    icon: <Hash size={12}/>,    isNumber: true },
    { k: "pocket_depth_max_mm",  label: t("industrialMetrics.fPocketDepth"), icon: <Box size={12}/>,     suffix: " mm", isNumber: true },
    { k: "slenderness_ratio",    label: t("industrialMetrics.fSlenderness"), icon: <Ruler size={12}/>,   suffix: " ×", isNumber: true },
  ];

  const levels = [
    { k: "vibration_risk",           label: t("industrialMetrics.lVibration"),   icon: <Activity size={12}/> },
    { k: "collision_risk",           label: t("industrialMetrics.lCollision"),   icon: <AlertCircle size={12}/> },
    { k: "material_deformation_risk",label: t("industrialMetrics.lDeformation"), icon: <Zap size={12}/> },
    { k: "fixturing_difficulty",     label: t("industrialMetrics.lFixturing"),   icon: <Wrench size={12}/> },
    { k: "tool_accessibility",       label: t("industrialMetrics.lToolAccess"),  icon: <Drill size={12}/> },
    { k: "chip_evacuation_difficulty", label: t("industrialMetrics.lChipEvac"),  icon: <Activity size={12}/> },
    { k: "tolerance_criticality",    label: t("industrialMetrics.lTolCrit"),     icon: <Ruler size={12}/> },
  ];

  return (
    <div className="space-y-4" data-testid="industrial-metrics">
      <div className="tech-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/60 label-tech flex items-center gap-2">
          <Activity size={12} className="text-blue-400"/> {t("industrialMetrics.title")}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-800/40 p-px">
          {fields.map((f) => {
            const v = metrics[f.k];
            return (
              <div key={f.k} className="p-3 bg-slate-900" data-testid={`metric-${f.k}`}>
                <div className="flex items-center gap-1.5 text-slate-500 text-[10px] label-tech">{f.icon}{f.label}</div>
                <div className="font-mono-data text-slate-100 text-base mt-1">{v ?? "—"}{f.suffix || ""}</div>
              </div>
            );
          })}
          {levels.map((f) => (
            <div key={f.k} className="p-3 bg-slate-900" data-testid={`metric-${f.k}`}>
              <div className="flex items-center gap-1.5 text-slate-500 text-[10px] label-tech">{f.icon}{f.label}</div>
              <div className="mt-1.5"><LevelPill value={metrics[f.k]}/></div>
            </div>
          ))}
        </div>
      </div>

      {(productionRisks?.length || 0) > 0 && <RiskAnalysis risks={productionRisks}/>}
    </div>
  );
}

function RiskAnalysis({ risks }) {
  const { t } = useTranslation();
  return (
    <div className="tech-card overflow-hidden" data-testid="risk-analysis">
      <div className="px-5 py-3 border-b border-slate-700/60 label-tech flex items-center gap-2 text-red-400">
        <ShieldAlert size={12}/> Production Risk Analysis
        <span className="ml-auto text-[10px] text-slate-500 font-mono-data">{t("industrialMetrics.items", { n: risks.length })}</span>
      </div>
      <div className="divide-y divide-slate-800">
        {risks.map((r, i) => {
          const sev = r.severity || "info";
          const colors = {
            alert: { dot: "bg-red-500",   text: "text-red-300",   border: "border-l-red-500" },
            warn:  { dot: "bg-amber-500", text: "text-amber-300", border: "border-l-amber-500" },
            info:  { dot: "bg-blue-500",  text: "text-blue-300",  border: "border-l-blue-500" },
          }[sev] || { dot: "bg-slate-500", text: "text-slate-300", border: "border-l-slate-500" };
          return (
            <div key={`risk-${i}-${r.title || sev}`} className={`px-5 py-3 border-l-4 ${colors.border}`} data-testid={`prod-risk-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}/>
                <div className={`label-tech text-[10px] ${colors.text}`}>{sev.toUpperCase()}</div>
                <div className="font-display font-semibold text-slate-100">{r.title}</div>
              </div>
              <div className="text-sm text-slate-400 mt-1.5 ml-3.5">{r.body}</div>
              {r.mitigation && <div className="text-xs text-emerald-300/90 mt-1.5 ml-3.5">→ {r.mitigation}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
